import React, { createContext, useContext, useMemo } from 'react';

export type EditModeType = '1+order' | 'zero-order';

interface IEditModeContextValue {
  editMode: EditModeType;
  isFieldsDisabled: boolean;
  hasParticipantOrders: boolean;
}

const EditModeContext = createContext<IEditModeContextValue | undefined>(undefined);

interface IEditModeProviderProps {
  children: React.ReactNode;
  hasParticipantOrders?: boolean;
}

export const EditModeProvider: React.FC<IEditModeProviderProps> = ({
  children,
  hasParticipantOrders = true,
}) => {
  const editMode: EditModeType = hasParticipantOrders ? '1+order' : 'zero-order';
  const isFieldsDisabled = hasParticipantOrders;

  const value = useMemo(
    () => ({
      editMode,
      isFieldsDisabled,
      hasParticipantOrders,
    }),
    [editMode, hasParticipantOrders, isFieldsDisabled]
  );

  return <EditModeContext.Provider value={value}>{children}</EditModeContext.Provider>;
};

export const useEditMode = (): IEditModeContextValue => {
  const context = useContext(EditModeContext);

  if (!context) {
    throw new Error('useEditMode must be used within EditModeProvider');
  }

  return context;
};

export const useEditModeSafe = (): IEditModeContextValue | null => {
  const context = useContext(EditModeContext);
  return context ?? null;
};

EditModeProvider.displayName = 'EditModeProvider';
